/* ---------------------------------------------------------------------------
   Test results, and the register of hold points, NCRs and RFIs.

   LIFECYCLE.md `LC-53` and `LC-54`; the tables are migrations/0039. Shared by
   the API and the screens, so the pass a form shows is the pass the register
   stores and the act a button offers is the act the endpoint accepts.

   ── A PASS IS DERIVED FROM THE FIGURES ──

   Where the figures decide it, nobody types it. A concrete result passes when
   its strength reaches the strength required at its age; a pile load test when
   its settlement is within the allowed settlement; an integrity test by the
   class the tester gave. The comparison is the plain one, result against the
   single required value: a characteristic-strength assessment over a set of
   results is the laboratory's statistics, stated on its certificate, and not
   this register's to recompute. Only a certificate's compliance is stated,
   because the certificate IS the statement.

   ── ONE REGISTER, THREE KINDS ──

   A hold point is released; an NCR is closed; an RFI is answered, then closed.
   Who may do each is `ITEM_ACTS` below, which the endpoint enforces.
   --------------------------------------------------------------------------- */

import type { Capability } from './roles.ts';
import type { SiteItem, SiteTest } from './types.ts';

/* =========================================================================
   TEST RESULTS
   ========================================================================= */

export type TestKind =
  | 'concrete-cube'
  | 'concrete-cylinder'
  | 'pile-integrity'
  | 'pile-load'
  | 'strand-cert'
  | 'rebar-cert';

/** Which columns a kind fills. */
export type TestFamily = 'concrete' | 'integrity' | 'load' | 'certificate';

export interface TestKindOption {
  id: TestKind;
  label: string;
  family: TestFamily;
}

export const TEST_KINDS: readonly TestKindOption[] = [
  { id: 'concrete-cube', label: 'Concrete cube', family: 'concrete' },
  { id: 'concrete-cylinder', label: 'Concrete cylinder', family: 'concrete' },
  { id: 'pile-integrity', label: 'Pile integrity test', family: 'integrity' },
  { id: 'pile-load', label: 'Pile load test', family: 'load' },
  { id: 'strand-cert', label: 'Strand certificate', family: 'certificate' },
  { id: 'rebar-cert', label: 'Rebar certificate', family: 'certificate' },
];

const TEST_BY_ID = new Map(TEST_KINDS.map((k) => [k.id, k]));

export function isTestKind(value: unknown): value is TestKind {
  return typeof value === 'string' && TEST_BY_ID.has(value as TestKind);
}

export function testKindLabel(id: string): string {
  return TEST_BY_ID.get(id as TestKind)?.label ?? `${id} (unknown)`;
}

export function testFamily(id: string): TestFamily | null {
  return TEST_BY_ID.get(id as TestKind)?.family ?? null;
}

/** How a pile's integrity was tested. */
export const INTEGRITY_METHODS: readonly { id: string; label: string }[] = [
  { id: 'low-strain', label: 'Low-strain (sonic echo)' },
  { id: 'cross-hole', label: 'Cross-hole sonic logging' },
  { id: 'thermal', label: 'Thermal integrity profiling' },
];

/**
 * The class an integrity tester gives a pile, and whether it passes. The usual
 * four-way split: a minor anomaly is accepted and noted; a major one, or a pile
 * found defective, is a non-conformance.
 */
export const INTEGRITY_CLASSES: readonly { id: string; label: string; passes: boolean }[] = [
  { id: 'sound', label: 'Sound', passes: true },
  { id: 'minor-anomaly', label: 'Minor anomaly', passes: true },
  { id: 'major-anomaly', label: 'Major anomaly', passes: false },
  { id: 'defective', label: 'Defective', passes: false },
];

export function isIntegrityMethod(value: unknown): boolean {
  return INTEGRITY_METHODS.some((m) => m.id === value);
}

export function isIntegrityClass(value: unknown): boolean {
  return INTEGRITY_CLASSES.some((c) => c.id === value);
}

export function integrityMethodLabel(id: string | null): string {
  return INTEGRITY_METHODS.find((m) => m.id === id)?.label ?? id ?? '';
}

export function integrityClassLabel(id: string | null): string {
  return INTEGRITY_CLASSES.find((c) => c.id === id)?.label ?? id ?? '';
}

/** The figures a pass is derived from. */
export interface TestFigures {
  kind: string;
  strengthMpa?: number | null;
  requiredMpa?: number | null;
  resultClass?: string | null;
  settlementMm?: number | null;
  allowedMm?: number | null;
  /** A certificate's own statement of compliance. Read for certificates only. */
  complies?: boolean | null;
}

/**
 * Whether a result passes, or null where the figures to decide it are missing.
 * Never read from a typed pass except for a certificate.
 */
export function testPasses(t: TestFigures): boolean | null {
  switch (testFamily(t.kind)) {
    case 'concrete':
      if (t.strengthMpa == null || t.requiredMpa == null) return null;
      return t.strengthMpa >= t.requiredMpa;
    case 'integrity':
      return INTEGRITY_CLASSES.find((c) => c.id === t.resultClass)?.passes ?? null;
    case 'load':
      if (t.settlementMm == null || t.allowedMm == null) return null;
      return t.settlementMm <= t.allowedMm;
    case 'certificate':
      return t.complies ?? null;
    default:
      return null;
  }
}

/** The result in a phrase: "27.1 of 30 MPa at 28 d", "Major anomaly", "14.2 of 12 mm". */
export function testResultText(t: SiteTest): string {
  switch (testFamily(t.kind)) {
    case 'concrete':
      return `${fig(t.strengthMpa)} of ${fig(t.requiredMpa)} MPa at ${t.ageDays ?? '?'} d`;
    case 'integrity':
      return integrityClassLabel(t.resultClass);
    case 'load':
      return `${fig(t.settlementMm)} of ${fig(t.allowedMm)} mm at ${fig(t.testLoadKn)} kN`;
    case 'certificate':
      return [t.grade, t.lot ? `lot ${t.lot}` : null].filter(Boolean).join(' · ');
    default:
      return '';
  }
}

/** What a test was on: the mix, the part, or the lot. */
export function testSubject(t: SiteTest): string {
  if (testFamily(t.kind) === 'concrete') return t.mix ?? '';
  if (testFamily(t.kind) === 'certificate') return t.grade ?? '';
  return t.component ?? '';
}

/** The title of the NCR a failed result raises. */
export function failedTestTitle(t: SiteTest): string {
  const on = testSubject(t);
  return `Failed ${testKindLabel(t.kind).toLowerCase()}${on ? ` (${on})` : ''}: ${testResultText(t)}`;
}

function fig(v: number | null | undefined): string {
  if (v == null) return '?';
  return String(Math.round(v * 10) / 10);
}

/**
 * The activities whose entries are pours, which a concrete result is linked to:
 * every concreting activity, by its unit of cubic metres outside the earthworks,
 * and the two precast ones counted by number.
 */
export const POUR_ACTIVITIES: ReadonlySet<string> = new Set([
  'pile-concreted',
  'pilecap-poured',
  'footing-poured',
  'concrete-poured',
  'girder-cast',
  'diaphragm-cast',
  'deck-poured',
]);

/** One mix's concrete results, oldest first, for the strength trend. */
export interface MixSeries {
  mix: string;
  results: SiteTest[];
}

/**
 * Concrete results grouped by mix, each oldest first, the mixes by name. A result
 * with no mix is its own group rather than dropped.
 */
export function strengthByMix(tests: readonly SiteTest[]): MixSeries[] {
  const by = new Map<string, SiteTest[]>();
  for (const t of tests) {
    if (testFamily(t.kind) !== 'concrete' || t.strengthMpa == null) continue;
    const mix = t.mix?.trim() || 'Mix not recorded';
    const list = by.get(mix) ?? [];
    list.push(t);
    by.set(mix, list);
  }
  return [...by.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([mix, results]) => ({
      mix,
      results: [...results].sort((a, b) => a.testedOn.localeCompare(b.testedOn)),
    }));
}

/* =========================================================================
   HOLD POINTS, NCRs AND RFIs
   ========================================================================= */

export type ItemKind = 'hold-point' | 'ncr' | 'rfi';
export type ItemStatus = 'open' | 'answered' | 'closed';
export type ItemAct = 'answer' | 'close' | 'release';

export interface ItemKindOption {
  id: ItemKind;
  label: string;
  /** The reference's prefix: HP-001. */
  prefix: string;
  /** Who raises one. */
  raise: Capability;
}

/**
 * The three kinds. A hold point is called by whoever records the works — the
 * site agent says the work is ready for inspection — and released by the
 * resident engineer; an NCR is the resident engineer's to raise and close; an
 * RFI is the site's to ask and the resident engineer's to answer and close.
 */
export const ITEM_KINDS: readonly ItemKindOption[] = [
  { id: 'hold-point', label: 'Hold point', prefix: 'HP', raise: 'progress.record' },
  { id: 'ncr', label: 'Non-conformance report', prefix: 'NCR', raise: 'works.supervise' },
  { id: 'rfi', label: 'Request for information', prefix: 'RFI', raise: 'progress.record' },
];

const ITEM_BY_ID = new Map(ITEM_KINDS.map((k) => [k.id, k]));

export function isItemKind(value: unknown): value is ItemKind {
  return typeof value === 'string' && ITEM_BY_ID.has(value as ItemKind);
}

export function itemKindLabel(id: string): string {
  return ITEM_BY_ID.get(id as ItemKind)?.label ?? `${id} (unknown)`;
}

/** HP-001, NCR-012, RFI-104. */
export function itemRef(kind: ItemKind, seq: number): string {
  return `${ITEM_BY_ID.get(kind)!.prefix}-${String(seq).padStart(3, '0')}`;
}

/** Who raises an item of this kind. */
export function raiseCapability(kind: ItemKind): Capability {
  return ITEM_BY_ID.get(kind)!.raise;
}

/**
 * The act each kind takes from each status, and where it goes. Every act is the
 * resident engineer's (`works.supervise`). A release is also refused to the
 * person who raised the hold point — see functions/lib/siteQuality.ts.
 */
export const ITEM_ACTS: Readonly<Record<ItemKind, Partial<Record<ItemStatus, { act: ItemAct; to: ItemStatus }>>>> = {
  'hold-point': { open: { act: 'release', to: 'closed' } },
  ncr: { open: { act: 'close', to: 'closed' } },
  rfi: { open: { act: 'answer', to: 'answered' }, answered: { act: 'close', to: 'closed' } },
};

/** The act open to an item as it stands, or null when it is closed. */
export function nextAct(item: Pick<SiteItem, 'kind' | 'status'>): { act: ItemAct; to: ItemStatus } | null {
  return ITEM_ACTS[item.kind as ItemKind]?.[item.status as ItemStatus] ?? null;
}

export const ITEM_ACT_LABEL: Record<ItemAct, string> = {
  answer: 'Answer',
  close: 'Close',
  release: 'Release',
};

export const ITEM_STATUS_LABEL: Record<ItemStatus, string> = {
  open: 'Open',
  answered: 'Answered',
  closed: 'Closed',
};

/** Open hold points against a baseline activity, by activity id. */
export function holdsByActivity(items: readonly SiteItem[]): Map<string, SiteItem[]> {
  const out = new Map<string, SiteItem[]>();
  for (const i of items) {
    if (i.kind !== 'hold-point' || i.status === 'closed' || !i.activityId) continue;
    const list = out.get(i.activityId) ?? [];
    list.push(i);
    out.set(i.activityId, list);
  }
  return out;
}
